import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Rise, MaskReveal, Overline } from "@/components/Reveal";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = "Please share your name.";
    if (!/^\S+@\S+\.\S+$/.test(values.email)) next.email = "A valid email, please.";
    if (!values.subject.trim()) next.subject = "A short subject helps.";
    if (!values.message.trim() || values.message.trim().length < 5) next.message = "Just a line or two, please.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...values, form_type: "contact" });
      setSent(true);
      setValues(initial);
      toast.success("Thanks — your message is on its way.");
    } catch (err) {
      toast.error("Sorry, something went wrong. Please try again or call the shop.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[color:var(--cream)]">
      <Seo
        title="Contact — Ring the Abattoir, Shop or Send a Message"
        description="Contact F. Drury & Sons in Tockenham, Wiltshire. Telephone 01793 852467. Address: The Abattoir, Tockenham, Wootton Bassett, Swindon, SN4 7PF. Send a message via our contact form."
        path="/contact"
        keywords="F Drury contact, butcher Swindon phone, Tockenham abattoir contact, butcher Wiltshire enquiry"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact F. Drury & Sons",
            url: "https://fdruryandsons.co.uk/contact",
            isPartOf: { "@id": "https://fdruryandsons.co.uk/#website" },
            about: { "@id": "https://fdruryandsons.co.uk/#localbusiness" },
            inLanguage: "en-GB",
          },
        ]}
      />
      {/* Hero */}
      <section className="pt-32 md:pt-48 pb-12 md:pb-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <MaskReveal><Overline>Contact · The Abattoir, Tockenham</Overline></MaskReveal>
            <h1 className="mt-6 md:mt-8 font-display tracking-tightest leading-[0.88] text-[16vw] sm:text-[13vw] md:text-[9.5vw] text-[color:var(--ink)]">
              <MaskReveal delay={0.1}><span>Whatever</span></MaskReveal>
              <MaskReveal delay={0.2}><span>the enquiry,</span></MaskReveal>
              <MaskReveal delay={0.3}><span className="italic text-[color:var(--oxblood)]">write to us.</span></MaskReveal>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 md:pt-12">
            <MaskReveal delay={0.35}>
              <p className="font-body italic text-lg md:text-xl leading-snug text-[color:var(--ink-light)] max-w-md mt-4 md:mt-0">
                Large order or small — a question about traceability, or a Sunday joint. The team gets back to every message.
              </p>
            </MaskReveal>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-20 md:pb-32">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-8 md:gap-16">
          {/* Info */}
          <div className="col-span-12 md:col-span-5">
            <Rise>
              <div className="border-t border-[color:var(--hairline)] pt-8">
                <Overline>Visit</Overline>
                <address className="not-italic mt-4 font-body text-xl md:text-2xl leading-snug text-[color:var(--ink)]">
                  The Abattoir, Tockenham,<br />
                  Wootton Bassett, Swindon,<br />
                  Wiltshire, SN4 7PF
                </address>
              </div>
            </Rise>

            <Rise delay={0.05}>
              <div className="border-t border-[color:var(--hairline)] pt-8 mt-10">
                <Overline>By phone</Overline>
                <ul className="mt-4 space-y-3">
                  <li className="flex items-baseline justify-between gap-3 md:gap-6">
                    <span className="font-body italic text-[color:var(--ink-light)] text-sm md:text-base">Abattoir &amp; office</span>
                    <a href="tel:01793852467" className="font-display italic text-xl md:text-3xl text-[color:var(--ink)] link-underline">01793 852467</a>
                  </li>
                </ul>
              </div>
            </Rise>

            <Rise delay={0.1}>
              <div className="border-t border-[color:var(--hairline)] pt-8 mt-10">
                <Overline>Office hours</Overline>
                <div className="mt-3 md:mt-4 font-display text-2xl md:text-4xl italic text-[color:var(--ink)]">Mon – Fri · 8am – 4pm</div>
                <p className="mt-3 md:mt-4 font-body italic text-[color:var(--ink-light)] text-sm md:text-base">
                  Please ring ahead if you're planning to visit the abattoir office.
                </p>
              </div>
            </Rise>

            <Rise delay={0.15}>
              <div className="border-t border-b border-[color:var(--hairline)] py-8 mt-10">
                <Overline>Find us</Overline>
                <div className="mt-4 aspect-[4/3] w-full clip-frame">
                  <iframe
                    title="F Drury and Sons — Tockenham map"
                    src="https://www.google.com/maps?q=Tockenham,+Wootton+Bassett,+Swindon,+Wiltshire,+SN4+7PF&output=embed"
                    className="w-full h-full border-0 grayscale contrast-110"
                    loading="lazy"
                  />
                </div>
              </div>
            </Rise>
          </div>

          {/* Form */}
          <div className="col-span-12 md:col-span-7 md:pl-8">
            <Rise>
              <div className="border-t border-[color:var(--hairline)] pt-8">
                <Overline>Send a message</Overline>
                <h2 className="mt-3 md:mt-4 font-display text-[9vw] sm:text-3xl md:text-5xl leading-[1.02] tracking-tightest">
                  We reply personally — <span className="italic text-[color:var(--oxblood)]">no automations.</span>
                </h2>
              </div>
            </Rise>

            {sent ? (
              <Rise>
                <div
                  data-testid={CONTACT.success}
                  className="mt-12 border border-[color:var(--oxblood)] p-10 bg-[color:var(--paper)]"
                >
                  <div className="font-display italic text-3xl md:text-4xl text-[color:var(--oxblood)]">Thank you.</div>
                  <p className="mt-4 font-body text-lg text-[color:var(--ink-light)]">
                    Your message is on its way. We'll be in touch shortly — often within the same working day.
                  </p>
                  <button
                    type="button"
                    className="btn-sharp-outline mt-8"
                    onClick={() => setSent(false)}
                  >
                    Send another
                  </button>
                </div>
              </Rise>
            ) : (
              <form
                data-testid={CONTACT.form}
                onSubmit={onSubmit}
                className="mt-12 space-y-2"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Your name*</label>
                    <input
                      className="input-line"
                      data-testid={CONTACT.inputName}
                      value={values.name}
                      onChange={set("name")}
                      placeholder="First & last"
                      required
                    />
                    {errors.name && <div className="mt-1 font-body italic text-sm text-[color:var(--oxblood)]">{errors.name}</div>}
                  </div>
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Email*</label>
                    <input
                      className="input-line"
                      type="email"
                      data-testid={CONTACT.inputEmail}
                      value={values.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      required
                    />
                    {errors.email && <div className="mt-1 font-body italic text-sm text-[color:var(--oxblood)]">{errors.email}</div>}
                  </div>
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Phone</label>
                    <input
                      className="input-line"
                      type="tel"
                      data-testid={CONTACT.inputPhone}
                      value={values.phone}
                      onChange={set("phone")}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Subject*</label>
                    <input
                      className="input-line"
                      data-testid={CONTACT.inputSubject}
                      value={values.subject}
                      onChange={set("subject")}
                      placeholder="Reserve, wholesale, general…"
                      required
                    />
                    {errors.subject && <div className="mt-1 font-body italic text-sm text-[color:var(--oxblood)]">{errors.subject}</div>}
                  </div>
                </div>
                <div className="pt-6">
                  <label className="font-ui text-[11px] uppercase tracking-editorial text-[color:var(--ink-light)]">Message*</label>
                  <textarea
                    className="input-line"
                    rows={5}
                    data-testid={CONTACT.inputMessage}
                    value={values.message}
                    onChange={set("message")}
                    placeholder="Tell us what you'd like…"
                    required
                  />
                  {errors.message && <div className="mt-1 font-body italic text-sm text-[color:var(--oxblood)]">{errors.message}</div>}
                </div>

                <div className="pt-10 flex flex-wrap items-center gap-5">
                  <button
                    type="submit"
                    data-testid={CONTACT.submit}
                    className="btn-sharp"
                    disabled={submitting}
                  >
                    <span>{submitting ? "Sending…" : "Send message"}</span>
                    {!submitting && (
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none"><path d="M0 5h13M9 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" /></svg>
                    )}
                  </button>
                  <span className="font-body italic text-sm text-[color:var(--ink-light)]">
                    Or ring the office directly — 01793 852467.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
